import { PlusOutlined } from '@ant-design/icons'
import { Divider, Tag } from 'antd'
import clsx from 'clsx'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useDebounce } from 'use-debounce'

import styles from './search-user.module.scss'
import { PureButton } from '../../../../common/ui/pure-button/pure-button'
import { PureSelect } from '../../../../common/ui/pure-select/pure-select'
import { useGetUsers, useInviteUser } from '../../../../entities/user/hooks/user.hooks'
import { useUserStore } from '../../../../entities/user/stores/user.store'
import { User } from '../../../../entities/user/types/user.types'
import { validateEmail } from '../../../../utils/validators/email.validator'

type SearchUserProps = {
  label?: string
  colClass?: string
  required?: boolean
  error?: string
  disabled?: boolean
  value?: number
  isInvited?: boolean
  onChange?: (email: string) => void
}

export const SearchUser = ({ label, colClass, required, error, value, isInvited = false, disabled = false, onChange }: SearchUserProps) => {
  const { t } = useTranslation()
  const [selectedUser, setSelectedUser] = useState<Partial<User> | null>(null)
  const { data: users, isLoading } = useGetUsers(true)
  const { mutateAsync: inviteUser } = useInviteUser()
  const { user } = useUserStore()
  const [searchValue, setSearchValue] = useState<string>('')
  const [debouncedSearch] = useDebounce(searchValue, 300)

  const readyUsers = useMemo(() => {
    return (
      users
        ?.filter(
          (user) =>
            user?.fio?.toLowerCase()?.includes(debouncedSearch?.toLowerCase()) ||
            user?.email?.toLowerCase()?.includes(debouncedSearch?.toLowerCase()),
        )
        .map((user) => ({
          value: user.email,
          display: user.fio || user.email,
          label: (
            <div className={clsx('d-flex justify-content-between flex-column', styles.labelWrapper)}>
              <span style={{ fontWeight: 600, fontSize: '14px', lineHeight: '20px' }}>{user.fio}</span>
              <span>
                <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
                  {t('common.email')}: {user.email}
                </span>
              </span>
            </div>
          ),
        })) ?? []
    )
  }, [users, debouncedSearch])

  useEffect(() => {
    let selectedUser = users?.find((user) => user.id === value)
    if (isInvited) {
      selectedUser = users?.find((user) => user.invitationId === value)
    }
    if (selectedUser) {
      setSelectedUser(selectedUser)
    }
  }, [users])

  const handleSearch = (search: string) => {
    setSearchValue(search)
  }

  const handleChange = (value: string) => {
    const user = users?.find((user) => user.email === value)

    if (!user) {
      toast.error(t('common.userNotFound'))

      return
    }

    setSelectedUser(user)

    onChange?.(user.email)
  }

  const handleInvite = async () => {
    if (!validateEmail(debouncedSearch)) {
      toast.error(t('common.errors.enterValidEmail'))

      return
    }

    try {
      const invitedUser = await inviteUser({
        email: debouncedSearch,
        roles: [],
        projects: user?.projectId ? [user?.projectId] : [],
      })

      setSelectedUser({
        id: 0,
        invitationId: invitedUser.id,
        email: invitedUser.email,
      })

      onChange?.(invitedUser.email)

      toast.success(t('usersPage.addNewUser.inviteUserSuccess'))
    } catch {
      toast.error(t('usersPage.addNewUser.errors.cannotInviteUser'))
    }
  }

  return (
    <>
      <PureSelect
        colClass={colClass}
        disabled={disabled}
        dropdownRender={(menu) => {
          const isEmpty = !readyUsers.length

          return (
            <>
              {menu}
              {isEmpty ? (
                <>
                  <Divider style={{ margin: '8px 0' }} />
                  <div style={{ padding: '0 8px 4px' }}>
                    <PureButton
                      icon={<PlusOutlined />}
                      onClick={handleInvite}
                      style={{ textAlign: 'left' }}
                      type="text"
                      block
                    >
                      {t('common.invite')}
                    </PureButton>
                  </div>
                </>
              ) : null}
            </>
          )
        }}
        error={error}
        filterOption={false}
        label={label}
        loading={isLoading}
        notFoundContent={<div className={clsx(styles.notFoundContent)}>{t('common.userNotFound')}</div>}
        onChange={handleChange}
        onSearch={handleSearch}
        optionLabelProp="display"
        options={readyUsers}
        placeholder=""
        required={required}
        searchValue={searchValue}
        size="small"
        value={selectedUser?.fio || selectedUser?.email}
        showSearch
      />
      {selectedUser?.isInvited ? (
        <Tag
          className={clsx('mt-2')}
          closable={false}
          color="volcano"
        >
          {t('workspace.responsibleHasNotAcceptedInvitation')}
        </Tag>
      ) : null}
    </>
  )
}
